import { formatTimeOfDay } from "../lib/format"

/**
 * В каком состоянии журнал и что в нём разрешено.
 *
 * ПОЧЕМУ ЭТО ОТДЕЛЬНЫЙ ЧИСТЫЙ ФАЙЛ. «Изменения недоступны» — не оформление, а правило
 * безопасности данных: запись, созданная без связи, неразрешимо конфликтует с реальной
 * занятостью (GAPS D4), и кабинет обязан не дать её создать, а не показать сообщение
 * после. Такое правило проверяется расчётом, а не глазами по снимку экрана.
 *
 * ГЛАВНОЕ ТРЕБОВАНИЕ МАКЕТА: кнопки ВИДИМО НЕДОСТУПНЫ, а не «нажимаются с ошибкой».
 * Поэтому функция отдаёт не только признак canEdit, но и ПРИЧИНУ словами: она уходит
 * в Button.deniedReason, то есть в title и в подпись для чтения с экрана. Кнопка,
 * выключенная молча, читается как поломка и кончается звонком в поддержку.
 */

export type JournalMode =
  /** Всё работает. */
  | "live"
  /** Связь пропала при открытом журнале: день на экране остаётся, изменения выключены. */
  | "offline"
  /** Журнал открыт без связи и собран из ранее загруженного: возраст данных — часы. */
  | "offline-cached"
  /** Платформа в режиме технических работ: чтение доступно, изменения выключены. */
  | "read_only"
  /** Кабинет недоступен целиком. */
  | "maintenance"

/**
 * Режим платформы (ADR-034).
 *
 * СЕГОДНЯ ЕГО НЕКОМУ ПРИСЛАТЬ: ни GET /v1/cabinet/auth/me, ни /health/ready, ни заголовок
 * ответа его не отдают — в API нет ни поля, ни кода. Поэтому значение приходит из двух
 * честных источников: кода отказа (когда сервер однажды начнёт отвечать 503 с кодом
 * read_only/maintenance) и параметра адреса ?degraded= для приёмки макета. Как только
 * поле появится в контексте кабинета, сюда добавится третий источник и ничего больше.
 */
export type PlatformState = "normal" | "read_only" | "maintenance"

export interface JournalModeInputs {
  /** Связь есть по мнению браузера. */
  readonly online: boolean
  /** Код последнего отказа загрузки дня. Транспортные коды означают «связи нет». */
  readonly problemCode: string | null
  /** День на экране есть. Без него показывается не полоса, а экран отказа. */
  readonly hasData: boolean
  readonly platform: PlatformState
  /** Сеанс поддержки только на чтение (CabinetContext.impersonation.readOnly). */
  readonly supportReadOnly: boolean
  /** Момент данных на экране (ScheduleResult.generatedAt). */
  readonly dataAt: string | null
  readonly timeZone: string
  /**
   * День собран не в этом сеансе, а из ранее сохранённого (П1-12).
   *
   * ДВА СЦЕНАРИЯ ОФЛАЙНА — ЭТО ДВА РАЗНЫХ ЭКРАНА, и различает их ровно этот признак
   * (ТЗ 05 §5.14): «связь пропала при открытом журнале» оставляет на экране день,
   * загруженный минуту назад, а «журнал открыт уже без связи» собирает день из кеша,
   * которому могут быть часы. Возраст данных показывается в обоих случаях, но текст
   * разный: «показано на 14:20» и «загружено сегодня в 09:15» — не одно и то же,
   * и человек, который по этому тексту решает, звонить ли клиенту, обязан видеть правду.
   *
   * Источник признака — отметка service worker на ответе (offline/register.ts),
   * а не догадка по navigator.onLine: браузер считает себя в сети и при мёртвом Wi-Fi.
   */
  readonly cached: boolean
  /** До какого времени идут работы. Сервер этого не сообщает — см. PlatformState. */
  readonly maintenanceUntil?: string | null
}

export interface ModeStripe {
  readonly tone: "offline" | "works"
  readonly title: string
  readonly text: string
  readonly actionLabel: string
}

/** Заглушка на всю рабочую область: журнала нет вовсе. */
export interface ModeBlocking {
  readonly title: string
  readonly text: string
  /** «Расписание, загруженное в 13:47, можно открыть для чтения» — либо пусто. */
  readonly readHint: string | null
  readonly readLabel: string
}

export interface JournalAvailability {
  readonly mode: JournalMode
  /** Можно ли менять день: создавать, переносить, отменять, отмечать. */
  readonly canEdit: boolean
  /**
   * Можно ли ОТКРЫТЬ запись на чтение.
   *
   * Это не то же самое, что canEdit, и разница взята из макета. В технических работах
   * «сетка и панель читаются», а все изменения выключены: администратор должен успеть
   * посмотреть состав визита и телефон клиента, чтобы позвонить ему голосом. В офлайне
   * панель НЕ открывается: её данные могли устареть, а ни одно действие в ней всё равно
   * не выполнится — открытая панель была бы обещанием, которое некому сдержать.
   */
  readonly canOpenDetails: boolean
  /** Причина словами. null — когда всё доступно. */
  readonly deniedReason: string | null
  readonly stripe: ModeStripe | null
  readonly blocking: ModeBlocking | null
}

/** Отказы, которые означают «связи нет», а не «сервер отказал по делу». */
const TRANSPORT_CODES: ReadonlySet<string> = new Set([
  "offline",
  "timeout",
  "server_error",
  "unknown",
])

export const OFFLINE_DENIED =
  "Пока нет связи, изменения недоступны — иначе запись может потеряться"

export const WORKS_DENIED =
  "Идут технические работы: расписание доступно для просмотра, изменения временно недоступны"

export const SUPPORT_DENIED =
  "Режим поддержки открыт только для чтения: изменения в кабинете салона недоступны"

function timeLabel(dataAt: string | null, timeZone: string): string | null {
  return dataAt === null ? null : formatTimeOfDay(dataAt, timeZone)
}

/**
 * Режим платформы из кода отказа.
 *
 * Отдельной функцией, потому что это единственное место, где кабинет вообще может
 * узнать о технических работах: сервер их пока не объявляет, и когда объявит — он
 * сделает это отказом, а не полем.
 */
export function platformFromProblem(problemCode: string | null): PlatformState {
  if (problemCode === "maintenance") return "maintenance"
  if (problemCode === "read_only") return "read_only"
  return "normal"
}

export function journalAvailability(inputs: JournalModeInputs): JournalAvailability {
  const at = timeLabel(inputs.dataAt, inputs.timeZone)

  if (inputs.platform === "maintenance") {
    const until =
      inputs.maintenanceUntil === null || inputs.maintenanceUntil === undefined
        ? null
        : formatTimeOfDay(inputs.maintenanceUntil, inputs.timeZone)
    // Час назван В ПОЯСЕ ФИЛИАЛА: formatTimeOfDay считает его по inputs.timeZone.
    // Подпись «по московскому времени» стояла здесь по привычке и врала каждому салону
    // восточнее Москвы ровно на его сдвиг — то есть тем, кто по этому часу планирует смену.
    const headline =
      until === null
        ? "Кабинет временно недоступен: идут плановые работы"
        : `Кабинет недоступен до ${until} по времени филиала`
    return {
      mode: "maintenance",
      canEdit: false,
      canOpenDetails: true,
      deniedReason: WORKS_DENIED,
      stripe: {
        tone: "offline",
        title: "Технические работы",
        text: headline,
        actionLabel: "Статус-страница",
      },
      blocking: {
        title: headline,
        text:
          "Идут плановые работы. Записи и данные салона сохранены, клиентам продолжают " +
          "уходить напоминания. Подробности — на статус-странице.",
        // Кнопка предлагается только тогда, когда открывать действительно есть что.
        readHint:
          inputs.hasData && at !== null
            ? `Расписание, загруженное в ${at}, можно открыть для чтения`
            : null,
        readLabel: "Открыть расписание для чтения",
      },
    }
  }

  if (inputs.platform === "read_only") {
    return {
      mode: "read_only",
      canEdit: false,
      canOpenDetails: true,
      deniedReason: WORKS_DENIED,
      stripe: {
        tone: "works",
        title: "Технические работы",
        text: "Расписание доступно для просмотра, изменения временно недоступны",
        actionLabel: "Статус-страница",
      },
      blocking: null,
    }
  }

  const offline = !inputs.online || (inputs.problemCode !== null && TRANSPORT_CODES.has(inputs.problemCode))

  if (offline) {
    const cached = inputs.cached
    return {
      mode: cached ? "offline-cached" : "offline",
      canEdit: false,
      /*
       * ПАНЕЛЬ ОТКРЫВАЕТСЯ ТОЛЬКО НА ДНЕ ИЗ КЕША, И ЭТО НЕ ПРОТИВОРЕЧИЕ.
       *
       * «Связь пропала при открытом журнале» (cached = false): макет 2-2.11.4 прямо
       * запрещает открывать панель — данные на экране собраны для сетки, часть панели
       * (телефон клиента по праву, история) требует ЗАПРОСА, и открытая панель обещала бы
       * то, чего не покажет.
       *
       * «Журнал открыт уже без связи» (cached = true): ТЗ 05 §5.14 требует обратного —
       * в кеше лежат «карточки их записей, контакты в пределах прав», и мастер в дальнем
       * кресле открывает визит, чтобы увидеть услугу и время. Это и есть фрейм 5-А3
       * «карточка на чтение» из пакета П1-12: панель показывает то, что уже сохранено,
       * и ни одного действия в ней не предлагает (canEdit остаётся false).
       */
      canOpenDetails: cached,
      deniedReason: OFFLINE_DENIED,
      stripe: {
        tone: "offline",
        title: "Нет связи",
        text: cached
          ? `Показано расписание, загруженное сегодня в ${at ?? "неизвестное время"}. Изменения недоступны — иначе запись может потеряться`
          : at === null
            ? "Расписание не загружено. Изменения сейчас не сохраняются"
            : `Расписание показано на ${at}, могло измениться. Изменения сейчас не сохраняются`,
        actionLabel: "Повторить",
      },
      blocking: null,
    }
  }

  if (inputs.supportReadOnly) {
    // Полосу режима поддержки рисует оболочка (она висит на всём кабинете, а не на журнале),
    // поэтому здесь остаётся только запрет действий и его причина.
    return {
      mode: "read_only",
      canEdit: false,
      // Поддержка пришла именно читать: панель ей нужна, действия — нет.
      canOpenDetails: true,
      deniedReason: SUPPORT_DENIED,
      stripe: null,
      blocking: null,
    }
  }

  return {
    mode: "live",
    canEdit: true,
    canOpenDetails: true,
    deniedReason: null,
    stripe: null,
    blocking: null,
  }
}

/** Разбор параметра приёмки ?degraded=read_only|maintenance. Чужое значение — не режим. */
export function parsePlatformState(value: unknown): PlatformState | undefined {
  if (value === "read_only" || value === "maintenance") return value
  return undefined
}
